import { useDefaultEditor } from "@/hooks/use-default-editor";
import { useListEditors } from "@/queries/editor-queries";
import { useListEnvironments } from "@/queries/environment-queries";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import { useProject } from "@/queries/project-queries";
import { useGetPreferenceByKey, useSetPreference } from "@/queries/user-preferences-queries";
import { EnvironmentPhase, ListEnvironmentsRequest_ArchivalStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, useMemo } from "react";

const DEFAULT_EDITOR_PER_ENV = "DEFAULT_EDITOR_PER_ENV";

export function useGetEditorForEnv(environmentId: string | undefined, projectId?: string) {
    const { editorId, editorVersion, isPending: isPendingEditorId } = useGetEditorIdForEnv(environmentId);
    const { data: policies, isLoading: isPoliciesLoading } = useGetOrganizationPolicies();
    const { data: editorsData, isPending: isPendingEditors } = useListEditors({
        allowedByPolicy: true,
    });
    const { data: project, isLoading: isProjectLoading } = useProject(projectId);

    const isSingleEditor = typeof editorsData?.editors.length === "number" && editorsData?.editors.length <= 1;
    const orgWideDefaultEditorId = policies?.defaultEditorId;

    const selectedEditor =
        editorsData?.editors.find((editor) => editor.id === editorId) ||
        editorsData?.editors.find((editor) => editor.id === orgWideDefaultEditorId) ||
        editorsData?.editors[0];

    // Build recommendedEditors map from project data
    const recommendedEditors = useMemo(() => {
        if (!project?.recommendedEditors) {
            return undefined;
        }
        return project.recommendedEditors;
    }, [project?.recommendedEditors]);

    return {
        selectedEditor,
        selectedEditorVersion: editorVersion,
        isSingleEditor,
        availableEditors: editorsData?.editors,
        recommendedEditors,
        isPending: isPendingEditors || isPoliciesLoading || isPendingEditorId || isProjectLoading,
    };
}

export function useGetEditorIdForEnv(environmentId: string | undefined) {
    const { editorId: defaultEditorId, isPending: isPendingDefaultEditorId } = useDefaultEditor();
    const { data: preference, isPending: isPendingPreferences } = useGetPreferenceByKey(DEFAULT_EDITOR_PER_ENV);

    let editorIdForEnv: string | undefined;
    let editorVersionForEnv: string | undefined;
    const editorPerEnvRaw = preference?.value;
    try {
        if (editorPerEnvRaw && environmentId) {
            const editorPerEnv = JSON.parse(editorPerEnvRaw) as [string, string, string?][];
            const entry = editorPerEnv.find(([k, _]) => k === environmentId);
            if (entry) {
                editorIdForEnv = entry[1];
                editorVersionForEnv = entry[2];
            }
        }
    } catch {
        console.error("failed to parse DEFAULT_EDITOR_PER_ENV user prefence");
    }

    return {
        editorId: editorIdForEnv || defaultEditorId,
        editorVersion: editorVersionForEnv,
        isPending: isPendingPreferences || isPendingDefaultEditorId,
    };
}

export function useSetEditorIdForEnv(environmentId: string | undefined) {
    const { editorId: defaultEditorId } = useDefaultEditor();
    const setPreference = useSetPreference();
    const { data: environmentsData } = useListEnvironments({
        archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ACTIVE,
    });
    const { data: editorPerEnvPreference } = useGetPreferenceByKey(DEFAULT_EDITOR_PER_ENV);
    const editorPerEnvRaw = editorPerEnvPreference?.value;

    return useCallback(
        async (editorId: string, version?: string) => {
            if (!environmentId || !environmentsData?.environments) {
                return;
            }
            try {
                let editorPerEnv: [string, string, string?][] = [];
                if (editorPerEnvRaw) {
                    editorPerEnv = JSON.parse(editorPerEnvRaw) as [string, string, string?][];
                }
                if (!Array.isArray(editorPerEnv)) {
                    editorPerEnv = [];
                }
                const environments = environmentsData.environments.filter(
                    (env) =>
                        env.spec?.desiredPhase !== EnvironmentPhase.DELETED &&
                        env.status?.phase !== EnvironmentPhase.DELETING,
                );
                let filteredEditorPerEnv = editorPerEnv.filter(
                    ([k, _]) => k !== environmentId && !!environments.find((e) => e.id === k),
                );
                if (editorId !== defaultEditorId) {
                    filteredEditorPerEnv.unshift([environmentId, editorId, version]);
                }
                const value = JSON.stringify(filteredEditorPerEnv.slice(0, 30));
                if (value.length >= 2480) {
                    // Guard against max length for user preference value defined in proto
                    filteredEditorPerEnv = filteredEditorPerEnv.slice(0, 20);
                }
                await setPreference.mutateAsync({
                    key: DEFAULT_EDITOR_PER_ENV,
                    value: JSON.stringify(filteredEditorPerEnv),
                });
            } catch (e) {
                console.error("Failed to update editor preference for env", e);
            }
        },
        [setPreference, environmentId, defaultEditorId, editorPerEnvRaw, environmentsData],
    );
}
